/* eslint-disable max-lines */
import React, { Fragment, useEffect, useState } from 'react';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import WarningIcon from '@quintoandar/cozy-icons/Warning';
import Typography from '@quintoandar/cozy-core/Typography';

import ROUTES from 'routes/constants';
import { getUser } from 'utils/user';
import AppBar from 'components/AppBar';
import PageWrapper from 'components/PageWrapper';
import DynamicForm from 'components/DynamicForm';
import { LoadingPage } from 'components/LoadingPage';
import { IconDescription } from 'components/IconDescription';
import HouseAddressV2 from './HouseAddressV2';
import Contacts from './Contacts';

import {
  checklistExternalAreaPageViewedAnalytics,
  trackChecklistExternalAreaFinishButtonClickedAction,
} from './actions';

import { fetchPhotoSessionDetailAction } from '../../PhotoSessionDetail/actions';
import { selectPhotoSessionDetail } from '../../PhotoSessionDetail/selectors';

import {
  checklistItemChanged,
  updateStorageChecklistItem,
  fetchChecklistAction,
  fetchChecklistFromStorageAction,
  updateHouseAddressAction,
} from '../actions';

import { selectChecklist } from '../selectors';
import messages from './messages';
import messagesToCodHouse from './HouseAddress/messages';
import { HOUSE_ADDRESS_ITEMS } from './constants';
import { HOUSE_ADDRESS_ITEMS as HOUSE_ADDRESS_FIELD_KEYS } from './HouseAddress/constants';
import { SECTIONS_TYPE, DEBOUNCE_STORAGE_UPDATE_WAIT_TIME } from '../constants';
import { isExternalAreaChecklistReady } from '../utils/checklistStorage';
import { buildHouseAddressParamsFromPhotoSession } from '../utils/houseAddressMerge';

const shouldPersistHouseAddressItem = ({ itemSlug, value }) => {
  if (
    itemSlug === HOUSE_ADDRESS_ITEMS.FLOOR
    || itemSlug === HOUSE_ADDRESS_ITEMS.TYPE
  ) {
    return Boolean(value);
  }

  return true;
};

const persistHouseAddressItemToStorage = (dispatch, updateParams) => {
  const { itemSlug, value } = updateParams;

  if (!shouldPersistHouseAddressItem({ itemSlug, value })) {
    return;
  }

  dispatch(
    updateStorageChecklistItem({
      ...updateParams,
      itemSlug,
      value,
      editedByUser: itemSlug === HOUSE_ADDRESS_FIELD_KEYS.COMPLEMENT ? true : undefined,
    }),
  );
};

const debouncedHouseAddressPersistBySlug = {};

const scheduleHouseAddressPersist = (dispatch, updateParams) => {
  const { itemSlug } = updateParams;

  if (!debouncedHouseAddressPersistBySlug[itemSlug]) {
    debouncedHouseAddressPersistBySlug[itemSlug] = debounce(
      (disp, params) => persistHouseAddressItemToStorage(disp, params),
      DEBOUNCE_STORAGE_UPDATE_WAIT_TIME,
    );
  }

  debouncedHouseAddressPersistBySlug[itemSlug](dispatch, updateParams);
};

const debounceInputUpdateContacts = debounce((dispatch, updateParams) => {
  const contactsItemsKeys = Object.keys(updateParams.contactsItems);

  contactsItemsKeys.map(contactsItemsKey =>
    dispatch(
      updateStorageChecklistItem({
        ...updateParams,
        itemSlug: contactsItemsKey,
        value: updateParams.contactsItems[contactsItemsKey].value,
      }),
    ),
  );
}, DEBOUNCE_STORAGE_UPDATE_WAIT_TIME);

export const ExternalArea = ({
  goToChecklist,
  trackPageView,
  onChangeItemChecklist,
  checklist,
  photoSessionDetail,
  fetchPhotoSessionDetail,
  updateHouseAddress,
  onChangeItemHouseAddress,
  onChangeItemContacts,
  fetchChecklistItems,
  fetchChecklistFromStorage,
  trackChecklistExternalAreaFinishButtonClicked,
}) => {
  /* istanbul ignore next */
  const [shouldValidate, setShouldValidate] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const checkCondoNameIsValid = () => {
    const checklistHouseCondoName = checklist.fixed.house.items.condoName.value;
    const checkHasCondoNameIsValid = (checklistHouseCondoName || checklistHouseCondoName === null)
    && checklistHouseCondoName !== '';

    return checkHasCondoNameIsValid;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* istanbul ignore next */
  useEffect(
    () => {
      if (photoSessionDetail.id === undefined) {
        fetchPhotoSessionDetail();
        return;
      }

      updateHouseAddress(
        buildHouseAddressParamsFromPhotoSession(photoSessionDetail, {
          existingItems: checklist.fixed?.house?.items,
          onlyFillMissing: checklist.hasFetchedChecklistItemsFromStorage,
        }),
      );
    },
    [
      photoSessionDetail.id,
      checklist.hasFetchedChecklistItemsFromStorage,
    ],
  );

  useEffect(() => {
    if (!checklist.hasFetchedChecklistItems) {
      fetchChecklistItems();
    }

    fetchChecklistFromStorage({ userId: getUser().user_id });
    trackPageView();
  }, []);

  const isNewChecklist = location.href.includes('new-checklist');

  const onValidate = isDynamicFormValid => {
    const isValidForm = isDynamicFormValid && checkCondoNameIsValid();

    setIsValid(isValidForm);
    setShouldValidate(false);

    if (isValidForm) {
      goToChecklist(isNewChecklist);
    }
    trackChecklistExternalAreaFinishButtonClicked({
      isValid: isValidForm,
    });
  };

  const validateForm = () => setShouldValidate(true);

  const isFinished =
    checklist.hasFetchedChecklistItems &&
    isExternalAreaChecklistReady(checklist);

  const buildHouseId = {
    ...messagesToCodHouse.houseId,
    values: { houseId: photoSessionDetail.house.id },
  };

  const ContentComponent = () => (
    <Fragment>
      <PageWrapper>
        {photoSessionDetail.id !== undefined ? (
          <HouseAddressV2
            photoSessionDetail={photoSessionDetail}
            house={checklist.fixed.house}
            onChangeItem={onChangeItemHouseAddress()}
            shouldValidate={shouldValidate}
            checkCondoNameIsValid={checkCondoNameIsValid}
          />
        ) : null}
        <Contacts
          contacts={checklist.fixed.contacts}
          onChangeItem={onChangeItemContacts()}
        />
        {checklist.externalArea !== undefined ? (
          <DynamicForm
            sections={checklist.externalArea}
            onChange={onChangeItemChecklist()}
            onValidate={onValidate}
            shouldValidate={shouldValidate}
          />
        ) : null}
        <Box mt={4} mb={2}>
          <Typography level="bodyStrong">
            <FormattedMessage {...buildHouseId} />
          </Typography>
        </Box>
        <Box my={4}>
          {!isValid ? (
            <IconDescription
              color="error"
              label={messages.invalid}
              icon={WarningIcon}
            />
          ) : null}
          <Button fullWidth onClick={validateForm}>
            <FormattedMessage {...messages.finishButton} />
          </Button>
        </Box>
      </PageWrapper>
    </Fragment>
  );

  return (
    <Fragment>
      <AppBar
        // eslint-disable-next-line react/jsx-no-bind
        onBack={() => goToChecklist(isNewChecklist)}
        title={messages.title}
      />
      {isFinished ? ContentComponent() : <LoadingPage />}
    </Fragment>
  );
};

ExternalArea.propTypes = {
  goToChecklist: PropTypes.func,
  trackChecklistExternalAreaFinishButtonClicked: PropTypes.func,
  trackPageView: PropTypes.func,
  onChangeItemChecklist: PropTypes.func,
  onChangeItemHouseAddress: PropTypes.func,
  onChangeItemContacts: PropTypes.func,
  updateHouseAddress: PropTypes.func,
  fetchChecklistItems: PropTypes.func,
  fetchChecklistFromStorage: PropTypes.func.isRequired,
  checklist: PropTypes.shape({
    externalArea: PropTypes.object,
  }),
  fetchPhotoSessionDetail: PropTypes.func.isRequired,
  photoSessionDetail: PropTypes.shape({
    status: PropTypes.string,
    id: PropTypes.number,
    showError: PropTypes.bool,
    house: PropTypes.shape({
      id: PropTypes.number,
    }).isRequired,
  }),
};

export const mapStateToProps = state => ({
  checklist: selectChecklist(state),
  photoSessionDetail: selectPhotoSessionDetail(state),
});

export const mapDispatchToProps = (dispatch, ownProps) => ({
  goToChecklist: isNewChecklist =>
    dispatch(
      push(
        isNewChecklist
          ? ROUTES.NEW_CHECKLIST(ownProps.params.id)
          : ROUTES.CHECKLIST(ownProps.params.id),
      ),
    ),
  trackPageView: () =>
    dispatch(
      checklistExternalAreaPageViewedAnalytics({
        photoSessionId: ownProps.params.id,
      }),
    ),
  trackChecklistExternalAreaFinishButtonClicked: ({ isValid }) =>
    dispatch(
      trackChecklistExternalAreaFinishButtonClickedAction({
        photoSessionId: ownProps.params.id,
        isValid,
      }),
    ),
  fetchPhotoSessionDetail: () =>
    dispatch(fetchPhotoSessionDetailAction(ownProps.params.id)),
  updateHouseAddress: ({ floor, complement, number, street, neighborhood }) =>
    dispatch(
      updateHouseAddressAction({
        floor,
        complement,
        number,
        street,
        neighborhood,
      }),
    ),
  fetchChecklistItems: () => dispatch(fetchChecklistAction()),
  fetchChecklistFromStorage: ({ userId }) =>
    dispatch(
      fetchChecklistFromStorageAction({
        userId,
        photoSessionId: ownProps.params.id,
      }),
    ),
  onChangeItemChecklist: (
    sectionType = SECTIONS_TYPE.EXTERNAL_AREA,
    userId = getUser().user_id,
  ) => ({ section, itemSlug, value, previousValue }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
      }),
    );
    dispatch(
      updateStorageChecklistItem({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
        userId,
      }),
    );
  },
  onChangeItemHouseAddress: (
    sectionType = SECTIONS_TYPE.FIXED,
    userId = getUser().user_id,
  ) => ({ section, itemSlug, value, houseAddressItems, previousValue }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
      }),
    );
    scheduleHouseAddressPersist(dispatch, {
      sectionType,
      section,
      itemSlug,
      value,
      houseAddressItems,
      previousValue,
      photoSessionId: ownProps.params.id,
      userId,
    });
  },
  onChangeItemContacts: (
    sectionType = SECTIONS_TYPE.FIXED,
    userId = getUser().user_id,
  ) => ({ section, itemSlug, value, contactsItems, previousValue }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
      }),
    );
    debounceInputUpdateContacts(dispatch, {
      sectionType,
      section,
      itemSlug,
      value,
      contactsItems,
      previousValue,
      photoSessionId: ownProps.params.id,
      userId,
    });
  },
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(ExternalArea);
